import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../components/layout/AdminLayout";
import BulkAddSigners from "../components/admin/BulkAddSigners";
import PdfPagePlacement from "../components/admin/PdfPagePlacement";
import DocumentProgressAdmin from "../components/admin/DocumentProgressAdmin";
import SignerList from "../components/admin/SignerList";
import AuditTimelineAdmin from "../components/admin/AuditTimelineAdmin";
import RevisionHistoryAdmin from "../components/admin/RevisionHistoryAdmin";
import ConfirmDialog from "../components/common/ConfirmDialog";
import EmptyStateCard from "../components/common/EmptyStateCard";
import SectionHeader from "../components/common/SectionHeader";
import { useToast } from "../components/common/ToastProvider";
import { SkeletonCard } from "../components/common/SkeletonBlock";
import { createDocumentEventSource } from "../api/realtimeApi";
import {
  createRevision,
  extendExpiry,
  getDocumentSummary,
  getSigners,
} from "../api/adminApi";
import { Icon } from "@iconify/react";

function formatDateTime(value) {
  if (!value) return "-";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

export default function DocumentWorkspacePage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [doc, setDoc] = useState(null);
  const [signers, setSigners] = useState([]);
  const [selectedSignerId, setSelectedSignerId] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [creatingRevision, setCreatingRevision] = useState(false);
  const [extendingExpiry, setExtendingExpiry] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showRevisionConfirm, setShowRevisionConfirm] = useState(false);
  const [showExtendExpiryConfirm, setShowExtendExpiryConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [liveConnected, setLiveConnected] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const liveSyncInFlightRef = useRef(false);
  const allSignersFinalized =
    signers.length > 0 &&
    signers.every((s) => {
      const status = String(s.status || "").toUpperCase();
      return (
        status === "SIGNED" || status === "REJECTED" || status === "EXPIRED"
      );
    });

  const loadWorkspace = useCallback(
    async (showErrors = true, fromLiveEvent = false) => {
      try {
        const [nextDoc, nextSigners] = await Promise.all([
          loadDocument(documentId),
          loadSigners(documentId),
        ]);
        setDoc(nextDoc);
        setSigners(nextSigners);
        setSelectedSignerId((prev) => {
          if (prev && nextSigners.some((s) => s.id === prev)) {
            return prev;
          }
          return nextSigners[0]?.id || "";
        });
        setLastRefreshedAt(new Date());
        setRefreshKey((prev) => prev + 1);
      } catch (e) {
        console.error("Failed to load workspace", e);
        if (showErrors) {
          toast.error("Failed to load document workspace.");
        }
      }
    },
    [documentId, toast],
  );

  useEffect(() => {
    if (!documentId) return;
    const docStatus = String(doc?.status || "").toUpperCase();
    if (
      docStatus === "SIGNED" ||
      docStatus === "REJECTED" ||
      docStatus === "EXPIRED"
    ) {
      return;
    }

    const init = async () => {
      setLoading(true);
      await loadWorkspace(true, false);
      setLoading(false);
    };

    init();
  }, [documentId, loadWorkspace, doc?.status]);

  useEffect(() => {
    if (!documentId) return;
    const docStatus = String(doc?.status || "").toUpperCase();
    if (
      docStatus === "SIGNED" ||
      docStatus === "REJECTED" ||
      docStatus === "EXPIRED" ||
      allSignersFinalized
    ) {
      return;
    }

    const eventSource = createDocumentEventSource(documentId);

    eventSource.addEventListener("connected", () => {
      setLiveConnected(true);
    });

    eventSource.addEventListener("document-update", async () => {
      try {
        if (allSignersFinalized) return;
        if (liveSyncInFlightRef.current) return;
        liveSyncInFlightRef.current = true;

        const res = await getSigners(documentId);
        const incoming = res.data || [];

        const byId = new Map(
          incoming.filter((s) => s?.id).map((s) => [s.id, s]),
        );
        if (byId.size === 0) return;

        setSigners((prev) =>
          prev.map((s) => {
            const next = byId.get(s.id);
            if (!next) return s;
            return {
              ...s,
              status: next.status ?? s.status,
              viewCount: next.viewCount ?? s.viewCount,
              firstViewedAt: next.firstViewedAt ?? s.firstViewedAt,
              lastActiveAt: next.lastActiveAt ?? s.lastActiveAt,
            };
          }),
        );

        setLastRefreshedAt(new Date());
      } catch (e) {
        console.error("Failed to process document-update", e);
      } finally {
        liveSyncInFlightRef.current = false;
      }
    });

    eventSource.onerror = () => {
      setLiveConnected(false);
    };

    return () => {
      eventSource.close();
      setLiveConnected(false);
    };
  }, [documentId, doc?.status, allSignersFinalized]);

  const loadDocument = async (id) => {
    const res = await getDocumentSummary(id);
    return res.data;
  };

  const loadSigners = async (id) => {
    const res = await getSigners(id);
    return res.data || [];
  };

  const handleBulkCompleted = async () => {
    await loadWorkspace(true, false);
    setActiveTab("placement");
    toast.success("Signers added successfully.");
  };

  const handleCreateRevision = async () => {
    if (!doc?.id) return;

    try {
      setCreatingRevision(true);
      const res = await createRevision(doc.id);
      toast.success("New revision created successfully.");
      setShowRevisionConfirm(false);
      navigate(`/documents/${res.data.id}`);
    } catch (e) {
      toast.error("Failed to create new revision.");
    } finally {
      setCreatingRevision(false);
    }
  };

  const handleExtendExpiry = async () => {
    if (!doc?.id) return;

    try {
      setExtendingExpiry(true);
      await extendExpiry(doc.id, 7);
      await loadWorkspace(true, false);
      setShowExtendExpiryConfirm(false);
      toast.success("Document expiry extended by 7 days.");
    } catch (e) {
      toast.error("Failed to extend expiry.");
    } finally {
      setExtendingExpiry(false);
    }
  };

  const handleOpenRevision = (revisionDoc) => {
    if (!revisionDoc?.id) return;
    navigate(`/documents/${revisionDoc.id}`);
  };

  if (loading || !doc) {
    return (
      <AdminLayout title="Document Workspace" subtitle="Loading document...">
        <SkeletonCard lines={5} titleWidth="40%" />
        <div className="section-space">
          <SkeletonCard lines={6} titleWidth="34%" />
        </div>
      </AdminLayout>
    );
  }

  const isLocked =
    doc.status.toUpperCase() === "SIGNED" ||
    doc.status.toUpperCase() === "REJECTED";

  const isRejected = doc.status.toUpperCase() === "REJECTED";
  const isExpired = doc.status.toUpperCase() === "EXPIRED";

  const overviewChips = [
    `Status: ${doc.status}`,
    `Revision: ${doc.revisionNo || 1}`,
    `Signers: ${signers.length}`,
  ];

  return (
    <>
      <AdminLayout
        title="Document Workspace"
        subtitle="Manage one document end to end."
      >
        <div className="workspace-breadcrumb">
          <span
            className="breadcrumb-link"
            onClick={() => navigate("/dashboard")}
          >
            <Icon icon="material-symbols:home-outline" width="16" height="16" />
          </span>
          <span className="breadcrumb-separator">/</span>
          <span
            className="breadcrumb-link"
            onClick={() => navigate("/documents")}
          >
            Documents
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{doc.name}</span>
        </div>

        <div className="workspace-hero-card">
          <div className="workspace-hero-top">
            <div>
              <h2 className="workspace-hero-title">{doc.name}</h2>
              <p className="workspace-hero-subtitle">
                Manage signers, field placement, progress, audit trail, and
                revisions.
              </p>
            </div>

            <div className="workspace-hero-actions">
              <button
                className="btn btn-light"
                onClick={() => loadWorkspace(true, false)}
              >
                Refresh Now
              </button>

              {isExpired && (
                <button
                  className="btn btn-primary"
                  onClick={() => setShowExtendExpiryConfirm(true)}
                  disabled={extendingExpiry}
                >
                  {extendingExpiry ? "Extending..." : "Extend Expiry"}
                </button>
              )}

              {isRejected && (
                <button
                  className="btn btn-primary"
                  onClick={() => setShowRevisionConfirm(true)}
                  disabled={creatingRevision}
                >
                  {creatingRevision ? "Creating..." : "Create Revision"}
                </button>
              )}
            </div>
          </div>
          <div className="workspace-meta-grid">
            <div className="workspace-meta-card">
              <div className="workspace-meta-label">Document ID</div>
              <div className="workspace-meta-value">{doc.id}</div>
            </div>

            <div className="workspace-meta-card">
              <div className="workspace-meta-label">Status</div>
              <div className="workspace-meta-value">
                <span
                  className={`badge badge-${String(doc.status).toLowerCase()}`}
                >
                  {doc.status}
                </span>
              </div>
            </div>

            <div className="workspace-meta-card">
              <div className="workspace-meta-label">Revision</div>
              <div className="workspace-meta-value">{doc.revisionNo || 1}</div>
            </div>

            <div className="workspace-meta-card">
              <div className="workspace-meta-label">Expires At</div>
              <div className="workspace-meta-value">
                {formatDateTime(doc.expiresAt)}
              </div>
            </div>
          </div>
          <div className="workspace-live-strip">
            <div className="workspace-live-pill">
              Live Status:{" "}
              <strong>{liveConnected ? "Connected" : "Disconnected"}</strong>
            </div>

            <div className="workspace-live-pill">
              Last Refresh:{" "}
              <strong>
                {lastRefreshedAt
                  ? new Intl.DateTimeFormat("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      second: "2-digit",
                    }).format(lastRefreshedAt)
                  : "-"}
              </strong>
            </div>
          </div>
          {isLocked && (
            <div className="info-banner" style={{ marginTop: 16 }}>
              This document is locked because it is{" "}
              <strong>{doc.status}</strong>. Signers, fields, and invite actions
              are disabled.
            </div>
          )}
          {isExpired && (
            <div className="warn-banner" style={{ marginTop: 16 }}>
              This document has expired. Extend expiry to continue the workflow.
            </div>
          )}
        </div>

        <div className="workspace-tabs">
          <button
            className={`workspace-tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={`workspace-tab ${activeTab === "placement" ? "active" : ""}`}
            onClick={() => setActiveTab("placement")}
          >
            Field Placement
          </button>
          <button
            className={`workspace-tab ${activeTab === "signers" ? "active" : ""}`}
            onClick={() => setActiveTab("signers")}
          >
            Signers
          </button>
          <button
            className={`workspace-tab ${activeTab === "progress" ? "active" : ""}`}
            onClick={() => setActiveTab("progress")}
          >
            Progress
          </button>
          <button
            className={`workspace-tab ${activeTab === "audit" ? "active" : ""}`}
            onClick={() => setActiveTab("audit")}
          >
            Audit
          </button>
          <button
            className={`workspace-tab ${activeTab === "revisions" ? "active" : ""}`}
            onClick={() => setActiveTab("revisions")}
          >
            Revisions
          </button>
        </div>

        {activeTab === "overview" && (
          <div className="section-space">
            <SectionHeader
              title="Overview (Bulk Add Signers)"
              subtitle="Add all document signers at once in the correct sequential order."
              chips={overviewChips}
            />
            {!isLocked && !isExpired ? (
              <BulkAddSigners
                documentId={doc.id}
                onCompleted={handleBulkCompleted}
              />
            ) : (
              <div className="card">
                <h3 className="card-title">Document Overview</h3>
                <p className="card-subtitle">
                  This document is currently locked for signer/field changes.
                </p>
                <EmptyStateCard
                  icon="🔒"
                  title="Editing is disabled"
                  message="You can still review progress, audit history, and revisions using the tabs above."
                  compact
                />
              </div>
            )}
          </div>
        )}

        {activeTab === "placement" && (
          <div className="section-space">
            <SectionHeader
              title="Field Placement"
              Field
              Placement
              subtitle="Assign signature areas to a selected signer."
              chips={[
                `Selected: ${
                  signers.find((s) => s.id === selectedSignerId)?.name || "None"
                }`,
              ]}
            />
            {signers.length > 0 ? (
              <>
                <div className="card section-space">
                  <h3 className="card-title">
                    Signer Selection for Field Placement
                  </h3>
                  <select
                    className="select"
                    value={selectedSignerId}
                    onChange={(e) => setSelectedSignerId(e.target.value)}
                    disabled={isLocked || isExpired}
                  >
                    <option value="">Select signer</option>
                    {signers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} - Order {s.signOrder}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedSignerId && (
                  <div className="section-space">
                    <PdfPagePlacement
                      documentId={doc.id}
                      signerId={selectedSignerId}
                      readOnly={isLocked || isExpired}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="card">
                <EmptyStateCard
                  icon="🖊️"
                  title="No signer selected for placement"
                  message="Add signers first, then choose one signer to place signature fields on the document."
                  compact
                />
              </div>
            )}
          </div>
        )}

        {activeTab === "signers" && (
          <div className="section-space">
            <SectionHeader
              title="Signers"
              subtitle="Review signer status, message history, activity, and send limits."
              chips={[`Total Signers: ${signers.length}`]}
            />
            <SignerList
              documentId={doc.id}
              refreshKey={refreshKey}
              signersData={signers}
              readOnly={isLocked || isExpired}
            />
          </div>
        )}

        {activeTab === "progress" && (
          <div className="section-space">
            <SectionHeader
              title="DocumentProgress"
              subtitle="Track overall document completion state."
              chips={[`Document: ${doc.status}`, `Signers: ${signers.length}`]}
            />
            <DocumentProgressAdmin
              documentId={doc.id}
              refreshKey={refreshKey}
            />
          </div>
        )}

        {activeTab === "audit" && (
          <div className="section-space">
            <SectionHeader
              title="Audit Trail"
              subtitle="Review important events performed on this document."
              chips={["Traceable History"]}
            />
            <AuditTimelineAdmin documentId={doc.id} refreshKey={refreshKey} />
          </div>
        )}

        {activeTab === "revisions" && (
          <div className="section-space">
            <SectionHeader
              title="Revision History"
              subtitle="Track document versions and open prior or newer revisions."
              chips={[`Current Revision: ${doc.revisionNo || 1}`]}
            />
            <RevisionHistoryAdmin
              documentId={doc.id}
              onOpen={handleOpenRevision}
            />
          </div>
        )}
      </AdminLayout>

      <ConfirmDialog
        open={showExtendExpiryConfirm}
        title="Extend Document Expiry"
        message="This will extend the document expiry by 7 days and allow the workflow to continue. Do you want to proceed?"
        confirmText="Extend Expiry"
        confirmClassName="btn btn-primary"
        loading={extendingExpiry}
        onConfirm={handleExtendExpiry}
        onCancel={() => setShowExtendExpiryConfirm(false)}
      />

      <ConfirmDialog
        open={showRevisionConfirm}
        title="Create New Revision"
        message="This will create a new revision of the document and copy the existing signer and field setup into the new version. Do you want to continue?"
        confirmText="Create Revision"
        confirmClassName="btn btn-primary"
        loading={creatingRevision}
        onConfirm={handleCreateRevision}
        onCancel={() => setShowRevisionConfirm(false)}
      />
    </>
  );
}
